import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import {
  ViewEncapsulation,
  HostListener,
  ElementRef,
  Component,
  OnDestroy,
  ViewChild,
  OnInit,
} from "@angular/core";

// Configurations
import { localToCmsLanguageConfigurations } from "src/app/configurations/main.configurations";

// Environments
import { environment } from "src/environments/environment";

// Libraries
import * as _ from "underscore";

// Pipes
import { GamesFilterPipe } from "src/app/modules/shared/pipes/games-filter.pipe";

// Models
import { LobbyGameGroup } from "src/app/modules/game-groups/models/lobby/lobby-game-group.model";
import { GameLaunch } from "src/app/modules/game-groups/models/game-launch/game-launch.model";
import { GameProviders } from "src/app/modules/game-groups/models/game-providers.model";
import { LobbyPregmatic } from "src/app/modules/game-groups/models/lobby/lobby.model";
import { ProviderPregmatic } from "src/app/modules/game-groups/models/provider.model";
import { GameGroup } from "src/app/modules/game-groups/models/game-group.model";
import { GamePregmatic } from "src/app/modules/game-groups/models/game.model";

// Services
import { RegistrationService } from "src/app/modules/registration/services/registration.service";
import { GameGroupsService } from "src/app/modules/game-groups/services/game-groups.service";
import { GamePlayService } from "src/app/modules/game-groups/services/game-play.service";
import { TranslationService } from "src/app/modules/shared/services/translation.service";
import { UserDetailsService } from "src/app/modules/user/services/user-details.service";
import { CommonService } from "src/app/modules/shared/services/common.service";
import { UtilityService } from "src/app/utility/utility.service";

@Component({
  selector: "app-view-all-game",
  templateUrl: "./view-all-games.component.html",
  styleUrls: ["./view-all-games.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ViewAllGamesComponent implements OnInit, OnDestroy {
  // View Child
  @ViewChild("pageHeader", { static: false }) pageHeader: ElementRef;
  @ViewChild("popupOver", { static: false }) popupOver: ElementRef;

  // Numbers
  userTotalBalance: number = 0;

  // Strings
  mediaUrlPath: string = environment.mediaUrlPath;
  websiteCode: string = environment.websiteCode;
  mediaUrl: string = environment.mediaUrl;
  currentPageGGroupUrl: string = "";
  activeProviderName: string = "";
  activeGroupName: string = "";
  currencySymbol: string = "";
  viewType: string = "games";
  providerName: string = "";
  languageCode: string = "";
  activeLobby: string = "";
  imgixParams: string = "";
  pageTitle: string = "";
  sortedBy: string = "";

  // Booleans
  isGameAvailable: boolean = false;
  isPageHeaderBg: boolean = false;
  isNameLoading: boolean = false;
  isShowBanner: boolean = true;
  isLoggedIn: boolean = false;
  isLoading: boolean = false;

  // Arrays
  beforeSortFilteredGameList: GamePregmatic[] = [];
  providerBeforeSortList: GameProviders[] = [];
  allFavoriteGamesList: GamePregmatic[] = [];
  filteredGamesList: GamePregmatic[] = [];
  providerList: GameProviders[] = [];
  lastPlayedGamesList: number[] = [];

  // Enums
  windowType: "desktop" | "mobile" = "desktop";
  deviceType: "desktop" | "mobile" = "desktop";

  // Objects
  gameGroupDetails: ProviderPregmatic | LobbyGameGroup;

  // Subscription
  subscriptions: Subscription[] = [];

  constructor(
    private registrationService: RegistrationService,
    private translationService: TranslationService,
    private userDetailsService: UserDetailsService,
    private gameGroupsService: GameGroupsService,
    private gamePlayService: GamePlayService,
    private gamesFilterPipe: GamesFilterPipe,
    private utilityService: UtilityService,
    private commonService: CommonService,
    private router: Router
  ) {
    this.onActiveLobbyOrProviderName();
  }

  // -----------------------------------------------------------------
  // Lifecycle Hooks
  ngOnInit(): void {
    this.onLobbyGroupsAndGames();

    this.getWindowType();

    this.imgixParams = this.utilityService.getImgixParams();

    this.isLoggedIn = this.utilityService.isUserLoggedIn();

    if (this.userDetailsService.getUserTotalBalance()) {
      this.userTotalBalance = this.userDetailsService.getUserTotalBalance();
    }

    this.subscriptions = [
      this.translationService.langCodeSb$.subscribe((languageCode: string) => {
        this.languageCode = languageCode;
      }),
      this.commonService.loginComplete$.subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      }),
      this.commonService.logOutComplete$.subscribe(() => {
        this.isLoggedIn = false;
      }),
      this.userDetailsService.currencySymbolSb$.subscribe(
        (currencySymbol: string) => {
          this.currencySymbol = currencySymbol;
        }
      ),
      this.userDetailsService.userBalanceUpdated$.subscribe(
        (balanceUpdateMessage: string) => {
          if (balanceUpdateMessage == "UserBalanceUpdated") {
            this.userTotalBalance = this.userDetailsService.getUserTotalBalance();

            this.utilityService.toggleBannerDepositBtn(this.userTotalBalance);
          }
        }
      ),
    ];
  }

  ngAfterViewInit(): void {
    this.onSetOverflowHiddenClass();
  }

  // -----------------------------------------------------------------
  // Host Listeners
  @HostListener("window:scroll") onScroll(): void {
    this.isPageHeaderBg = this.utilityService.scrollAddClass();
  }

  @HostListener("window:resize") onResize(): void {
    this.getWindowType();

    this.onSetOverflowHiddenClass();
  }

  // -----------------------------------------------------------------
  // Get Window Type
  getWindowType(): void {
    let clientWidth: number = document.body.clientWidth;

    if (clientWidth >= 1024) {
      this.windowType = "desktop";

      this.deviceType = "desktop";
    } else {
      this.windowType = "mobile";

      this.deviceType = "mobile";
    }

    if (
      (clientWidth >= 500 &&
        window.matchMedia("(orientation: landscape)").matches) ||
      (clientWidth >= 768 &&
        window.matchMedia("(orientation: portrait)").matches)
    ) {
      this.windowType = "desktop";
    }
  }

  // -----------------------------------------------------------------
  // Get Methods
  /*
    Functioanlity to get Game Group details based on active current page
  */
  getGamesbyGroupId(
    games: GamePregmatic[],
    gameGroupDetails: ProviderPregmatic
  ): GamePregmatic[] {
    let filteredGames: GamePregmatic[] = [];

    if (gameGroupDetails.group_type === "manual") {
      _.each(games, (game: GamePregmatic) => {
        if (game.gameGroupList) {
          _.each(
            game.gameGroupList,
            (list: GameGroup, indexGameGroup: number) => {
              if (list.id === gameGroupDetails.id) {
                if (game) {
                  let clonedGame: GamePregmatic = _.clone(game);

                  clonedGame.gameGroupList = game.gameGroupList[indexGameGroup];

                  filteredGames.push(clonedGame);
                }
                return;
              }
            }
          );
        }
      });
    } else if (
      gameGroupDetails.group_type === "automatic" &&
      gameGroupDetails.group_sub_type === "continue_playing"
    ) {
      if (
        this.isLoggedIn &&
        this.lastPlayedGamesList &&
        this.lastPlayedGamesList.length > 0
      ) {
        _.each(this.lastPlayedGamesList, (typeId: number) => {
          let game: GamePregmatic = _.findWhere(games, {
            beGameTypeId: typeId,
          });

          if (game) {
            filteredGames.push(game);
          }
        });

        filteredGames = this.gamesFilterPipe.transform(filteredGames, {
          typeOfGames: "non-live-game",
        });
      } else {
        this.filteredGamesList = [];
      }
    } else if (
      gameGroupDetails.group_type === "automatic" &&
      gameGroupDetails.group_sub_type === "favourite"
    ) {
      if (
        this.isLoggedIn &&
        this.allFavoriteGamesList &&
        this.allFavoriteGamesList.length > 0
      ) {
        _.each(this.allFavoriteGamesList, (favoriteGame: GamePregmatic) => {
          let game: GamePregmatic = _.findWhere(games, {
            beGameTypeId: favoriteGame.gameTypeId,
          });

          if (game) {
            filteredGames.push(game);
          }
        });
      } else {
        this.filteredGamesList = [];
      }
    }

    return filteredGames;
  }

  // -----------------------------------------------------------------
  // Set Methods
  onActiveLobbyOrProviderName(): void {
    const segmentsList: string[] = this.utilityService
      .getDecodedCurrentPath()
      .split("/");

    if (segmentsList[3]) {
      this.currentPageGGroupUrl = `${segmentsList[2]}/${segmentsList[3]}`;
    }

    let lobbyName: string = this.commonService.getActiveLobby();

    if (lobbyName) {
      this.activeLobby = this.utilityService.getActiveLobby(lobbyName);
    } else {
      this.onLobbyNamebyGGroupMutilingualURL(this.currentPageGGroupUrl);
    }

    this.commonService.broadCastActiveLeftMenuLobby(this.activeLobby);
  }

  onGoToFullScreen(targetEle: Element): void {
    this.utilityService.fullScreenInit(targetEle);
  }

  onGoOutFullScreen(isClose: boolean): void {
    if (isClose) {
      this.utilityService.exitFullScreen();

      this.utilityService.removeClassFromAppBody("overflow-hidden");
    }
  }

  onSetOverflowHiddenClass(): void {
    setTimeout(() => {
      if (
        document.querySelector(".page-body") &&
        this.deviceType === "mobile"
      ) {
        this.onGoToFullScreen(document.documentElement);

        this.utilityService.addClassToAppBody("overflow-hidden");
      } else {
        this.utilityService.removeClassFromAppBody("overflow-hidden");
      }
    });
  }

  onLobbyGroupsAndGames(isForce?: boolean): void {
    this.isLoading = true;

    let apiURL: Promise<any>[] = [
      this.gameGroupsService.getLobbyListWithGameGroupsp(isForce),
      this.gameGroupsService.getGameGroupGamesp(isForce),
    ];

    if (this.isLoggedIn) {
      apiURL.push(this.gameGroupsService.getLastedPlayedGamesp(false));
    }

    Promise.all(apiURL).then(
      ([lobbyGameGroupsResponse, gamesResponse, lastPlayedResponse]) => {
        let lobbyGameGroupsList: LobbyPregmatic[] = lobbyGameGroupsResponse;

        let games: GamePregmatic[] = gamesResponse;

        window["prerenderReady"] = true;

        if (lastPlayedResponse) {
          let lastPlayedList: number[] = lastPlayedResponse;

          this.lastPlayedGamesList = _.clone(lastPlayedList);
        }

        /*
          Funationality Get Active lobby Group details based on
          the active group name
        */
        lobbyGameGroupsList = JSON.parse(JSON.stringify(lobbyGameGroupsList));

        if (lobbyGameGroupsList && lobbyGameGroupsList.length > 0) {
          this.onGroupDetailsByGGMultilingualURL(
            _.clone(lobbyGameGroupsList),
            this.currentPageGGroupUrl
          );
        }

        /*
        Funationality Get all the games mapped with this
        active group name
        */
        if (this.gameGroupDetails && games) {
          games = JSON.parse(JSON.stringify(games));

          this.gameGroupDetails.games = this.getGamesbyGroupId(
            games,
            this.gameGroupDetails as ProviderPregmatic
          );

          if (this.gameGroupDetails.content) {
            this.gameGroupDetails.content = this.utilityService.replaceAll(
              this.gameGroupDetails.content,
              {
                "{{site_url}}": environment.siteUrl,
                "{{mediaUrl}}": environment.mediaUrl,
                "{{websiteCode}}": environment.websiteCode,
              }
            );

            setTimeout(() => {
              this.utilityService.toggleBannerDepositBtn(this.userTotalBalance);

              let gameGroupBanner: NodeListOf<Element> = document.querySelectorAll(
                ".gameGroupBanner"
              );

              if (gameGroupBanner && gameGroupBanner.length <= 0) {
                this.isShowBanner = false;
              }
            });
          }
        }

        /*
          Functionality to check whether games are available ot not...
          To show Message as "No games configured under {{}}"
        */
        if (this.gameGroupDetails && this.gameGroupDetails.games) {
          if (this.gameGroupDetails.game_asset_style === "quicklink") {
            if (this.providerName) {
              this.onShowGamesInProviders(this.providerName);
            } else {
              this.viewType = "providers";

              this.providerList = this.utilityService.getSortedUniqProviderList(
                this.gameGroupDetails.games
              );

              this.isGameAvailable = true;
            }
          } else if (
            (this.gameGroupDetails.group_type === "automatic" &&
              this.gameGroupDetails.group_sub_type === "continue_playing") ||
            (this.gameGroupDetails.group_type === "automatic" &&
              this.gameGroupDetails.group_sub_type === "favourite")
          ) {
            this.filteredGamesList = this.gameGroupDetails.games;

            this.isGameAvailable = true;
          } else {
            this.filteredGamesList = this.utilityService.sortGameByCountryOrder(
              this.gameGroupDetails.games
            );

            this.isGameAvailable = true;
          }
        } else {
          this.filteredGamesList = [];

          this.isGameAvailable = false;
        }

        this.isLoading = false;
      }
    );
  }

  onGroupDetailsByGGMultilingualURL(
    lobbyPregmaticList: LobbyPregmatic[],
    currentPageGGroupUrl: string
  ): void {
    lobbyPregmaticList.map((lobbyPregmatic: LobbyPregmatic) => {
      if (
        lobbyPregmatic.name.toLowerCase() === this.activeLobby &&
        lobbyPregmatic.gameGroupList.length
      ) {
        lobbyPregmatic.gameGroupList.map((group: LobbyGameGroup) => {
          if (
            group.multilingualUrlGameGroup &&
            group.multilingualUrlGameGroup[
              localToCmsLanguageConfigurations[this.languageCode]
            ] &&
            currentPageGGroupUrl
          ) {
            let multilingualUrlGameGroupName: string =
              group.multilingualUrlGameGroup[
                localToCmsLanguageConfigurations[this.languageCode]
              ];

            if (
              multilingualUrlGameGroupName.toLowerCase().trim() ===
              currentPageGGroupUrl.toLowerCase().trim()
            ) {
              this.gameGroupDetails = group;

              this.utilityService.setSEO(this.gameGroupDetails, true);

              this.commonService.setCanonicalURL(
                this.gameGroupDetails.multilingualUrlGameGroup
              );

              this.pageTitle = group.name;

              this.isNameLoading = false;

              return;
            }
          }
        });
      }
    });
  }

  /*
    Sort game name by alphabet(A-Z)
  */
  onSortByGameName(calledFrom: string): void {
    if (calledFrom === "games") {
      if (this.sortedBy !== "a-z") {
        this.sortedBy = "a-z";

        this.filteredGamesList = this.utilityService.sortByGameName(
          (this.gameGroupDetails as ProviderPregmatic).games,
          "name"
        );
      } else {
        this.sortedBy = "";

        this.filteredGamesList = this.utilityService.sortGameByCountryOrder(
          (this.gameGroupDetails as ProviderPregmatic).games
        );
      }
    } else if (calledFrom === "providers") {
      if (this.sortedBy !== "a-z") {
        this.sortedBy = "a-z";

        this.providerBeforeSortList = this.providerList;

        this.providerList = _.sortBy(this.providerList, "vendorDisplayName");
      } else {
        this.sortedBy = "";

        this.providerList = this.providerBeforeSortList;
      }
    } else {
      if (this.sortedBy !== "a-z") {
        this.sortedBy = "a-z";

        this.filteredGamesList = this.utilityService.sortByGameName(
          this.filteredGamesList,
          "name"
        );
      } else {
        this.sortedBy = "";

        this.filteredGamesList = this.beforeSortFilteredGameList;
      }
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /*
    Sort game by Release Date
  */
  onOrderByReleaseDate(): void {
    if (this.sortedBy !== "release-date") {
      this.sortedBy = "release-date";

      this.filteredGamesList = this.utilityService.sortByReleaseDate(
        (this.gameGroupDetails as ProviderPregmatic).games,
        "date"
      );
    } else {
      this.sortedBy = "";

      this.filteredGamesList = (this
        .gameGroupDetails as ProviderPregmatic).games;
    }
  }

  onShowGamesInProviders(providerName: string): void {
    providerName = this.utilityService.convertGameNameToUrl(providerName);

    this.router.navigate([`${this.languageCode}/studio/${providerName}`]);
  }

  /*
    Functaionality Go back to the respective lobby...
  */
  onGoBack(isClose: boolean): void {
    this.router.navigate([
      `${this.languageCode}/${this.translationService.instant("url.casino")}`,
    ]);

    this.onGoOutFullScreen(isClose);
  }

  onGameLaunch(gameName: string, hasDemo: boolean): void {
    gameName = this.utilityService.convertGameNameToUrl(gameName);

    this.gamePlayService.clearGameWindowData();

    setTimeout(() => {
      if (gameName && (this.isLoggedIn || (!this.isLoggedIn && hasDemo))) {
        this.router.navigate([
          `${this.languageCode}/${this.translationService.instant(
            "url.game"
          )}/${gameName}`,
        ]);
      } else {
        this.utilityService.initiateLogin();
      }
    });
  }

  onLobbyNamebyGGroupMutilingualURL(currentPageGGroupUrl: string): void {
    Promise.resolve(this.gameGroupsService.getLobbyListWithGameGroupsp()).then(
      (response: LobbyPregmatic[]) => {
        if (response && response.length > 0) {
          let lobbyWithGameGroupList: LobbyPregmatic[] = response;

          _.filter(lobbyWithGameGroupList, (lobby: LobbyPregmatic) => {
            if (lobby.gameGroupList) {
              lobby.gameGroupList.filter((lobbyGameGroup: LobbyGameGroup) => {
                if (
                  lobbyGameGroup &&
                  this.languageCode &&
                  lobbyGameGroup.multilingualUrlGameGroup &&
                  lobbyGameGroup.multilingualUrlGameGroup[
                    localToCmsLanguageConfigurations[this.languageCode]
                  ]
                ) {
                  let multilingualUrlGameGroupName: string =
                    lobbyGameGroup.multilingualUrlGameGroup[
                      localToCmsLanguageConfigurations[this.languageCode]
                    ];

                  if (
                    multilingualUrlGameGroupName.toLowerCase().trim() ===
                    currentPageGGroupUrl.toLowerCase().trim()
                  ) {
                    this.activeLobby = lobby.name;

                    if (this.activeLobby) {
                      this.activeLobby = this.utilityService.getActiveLobby(
                        this.activeLobby
                      );
                    }
                    return;
                  }
                }
              });
            }

            if (this.activeLobby) {
              return;
            }
          });
        }

        if (!this.activeLobby) {
          this.router.navigate([`${this.languageCode}/casino`]);
        }
      }
    );
  }

  onJoinBanners(event: Event): void {
    let target: HTMLInputElement = <HTMLInputElement>event.target;

    let routerLink: string = target.getAttribute("routerLink");

    let data: {
      bonusCode: string;
    };

    if (routerLink === "deposit" && target.getAttribute("bonusCode")) {
      data = { bonusCode: target.getAttribute("bonusCode") };
    }

    /*
      We are using this registration routing here because of circular
      dependency
    */
    if (
      routerLink &&
      (routerLink === "registration" || routerLink === "register")
    ) {
      if (
        !this.isLoggedIn &&
        environment.zimplerMarkets.indexOf(this.utilityService.getLangCode()) <=
          -1
      ) {
        this.registrationService.openRegistrationHanlder();
      } else {
        this.utilityService.initiateDeposit();
      }
    } else if (routerLink) {
      if (
        routerLink &&
        this.utilityService.getActiveLobby(routerLink.split("/")[1]) === "game"
      ) {
        this.gamePlayService.clearGameWindowData();
      }
      /*
        Below Utility Handler decide routerLink event action on banner
        like registration button , login btn, game play btn deposit etc...
      */
      this.utilityService.cmsRoutingHandler(routerLink, data);
    }
  }

  onTriggerGameLaunch(gameLaunch: GameLaunch): void {
    this.gamePlayService.setGameCalledfrom(gameLaunch.callingFrom);

    this.onGameLaunch(gameLaunch.name, gameLaunch.hasDemo);
  }

  // -----------------------------------------------------------------
  // On Destroy
  ngOnDestroy(): void {
    this.utilityService.removeClassFromAppBody("overflow-hidden");

    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
